/**
 * api-store.ts — the P2 endpoints, from the browser (P2 wave 4).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * ONE HTTP LAYER, AND THIS IS NOT IT
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Every function here goes through `apiRequest` in `./api-client`, which owns the fetch, the
 * status→kind mapping and `credentials: 'include'`. A second fetch wrapper would be a second answer
 * to "what does a 401 mean", and the whole point of `ApiFailureKind` is that the UI renders cases
 * rather than status codes.
 *
 * **Every response is parsed against the contract's schema**, exactly as P1's calc client parses
 * the envelope, and for the same reason (FR-24): a body that does not match means the API and the
 * browser disagree about the shape of an engineering record, and rendering half of it would put a
 * date under the wrong heading or a ratio under the wrong check.
 *
 * The two exceptions are better-auth's own endpoints. Their bodies are the library's shape, not
 * ours, and the contract deliberately does not describe them — inventing a schema for somebody
 * else's response would break on their next release for no gain. What we need from those calls is
 * the status code, and that is what the callers read.
 */

import {
  acceptInviteResponseSchema,
  caseListResponseSchema,
  caseResponseSchema,
  deleteCaseResponseSchema,
  importResponseSchema,
  meResponseSchema,
  projectListResponseSchema,
  projectResponseSchema,
  recalcConfirmResponseSchema,
  recalcPreviewResponseSchema,
  versionResponseSchema,
  type AcceptInviteResponse,
  type CaseListResponse,
  type CaseResponse,
  type DeleteCaseResponse,
  type ImportResponse,
  type MeResponse,
  type ProjectListResponse,
  type ProjectResponse,
  type RecalcConfirmResponse,
  type RecalcPreviewResponse,
  type SavedModule,
  type VersionResponse,
} from '@ronceray/contract';

import { apiRequest, type ApiResult } from './api-client';

/* ================================================================================================
 * Identity
 * ============================================================================================== */

/**
 * Who am I. **The application's first request, and the gate on everything else.**
 *
 * A `401` here is not an error to render — it is the answer "you are not signed in", and the shell
 * turns it into the sign-in view with the destination preserved.
 */
export function fetchMe(): Promise<ApiResult<MeResponse>> {
  return apiRequest('/v1/me', { method: 'GET', parse: (v) => meResponseSchema.parse(v) });
}

/** better-auth's own endpoint. Its body is the library's; the status is what we read. */
export function signIn(email: string, password: string): Promise<ApiResult<unknown>> {
  return apiRequest('/v1/auth/sign-in/email', {
    body: { email, password },
    parse: (v) => v,
  });
}

export function signOut(): Promise<ApiResult<unknown>> {
  return apiRequest('/v1/auth/sign-out', { body: {}, parse: (v) => v });
}

/**
 * Accept an invite — the only path by which an account comes into existence (D5).
 *
 * It does **not** sign the new user in: `autoSignIn` is off in `apps/api/src/auth.ts`, so the
 * caller follows this with {@link signIn}. That is a deliberate two-step rather than an oversight;
 * it means the password the user just chose is exercised immediately, while they are still
 * expecting to type it.
 */
export function acceptInvite(
  token: string,
  name: string,
  password: string,
): Promise<ApiResult<AcceptInviteResponse>> {
  return apiRequest('/v1/org/invites/accept', {
    body: { token, name, password },
    parse: (v) => acceptInviteResponseSchema.parse(v),
  });
}

/**
 * What the API is running now (`GET /v1/version`).
 *
 * Read once after sign-in, and compared against every stored case's own stamp to decide whether
 * US-215's banner appears. Parsed through the contract's schema like everything else: a banner that
 * appeared or vanished because the two sides disagreed about this body would be worse than none.
 */
export function fetchVersion(): Promise<ApiResult<VersionResponse>> {
  return apiRequest('/v1/version', {
    method: 'GET',
    parse: (v) => versionResponseSchema.parse(v),
  });
}

/* ================================================================================================
 * Projects
 * ============================================================================================== */

export function listProjects(
  options: { limit?: number; offset?: number } = {},
): Promise<ApiResult<ProjectListResponse>> {
  const query = new URLSearchParams();
  if (options.limit !== undefined) query.set('limit', String(options.limit));
  if (options.offset !== undefined) query.set('offset', String(options.offset));
  const suffix = query.size > 0 ? `?${query.toString()}` : '';

  return apiRequest(`/v1/projects${suffix}`, {
    method: 'GET',
    parse: (v) => projectListResponseSchema.parse(v),
  });
}

export function createProject(name: string, address?: string): Promise<ApiResult<ProjectResponse>> {
  return apiRequest('/v1/projects', {
    body: address === undefined || address === '' ? { name } : { name, address },
    parse: (v) => projectResponseSchema.parse(v),
  });
}

export function listCases(projectId: string): Promise<ApiResult<CaseListResponse>> {
  return apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/cases`, {
    method: 'GET',
    parse: (v) => caseListResponseSchema.parse(v),
  });
}

/* ================================================================================================
 * Cases
 * ============================================================================================== */

/**
 * Save the current form to a project.
 *
 * Note what is **not** in the body: the results. The server recomputes them (US-206), and sending
 * them would be refused with a 422 rather than silently dropped. That refusal is a feature — it
 * says "the server computes this" at the only moment anybody is listening.
 */
export function saveCase(
  projectId: string,
  body: { module: SavedModule; title: string | null; inputs: Record<string, unknown> },
): Promise<ApiResult<CaseResponse>> {
  return apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/cases`, {
    body,
    parse: (v) => caseResponseSchema.parse(v),
  });
}

/** The **stored** snapshot, never a fresh calculation (D10). */
export function fetchCase(caseId: string): Promise<ApiResult<CaseResponse>> {
  return apiRequest(`/v1/cases/${encodeURIComponent(caseId)}`, {
    method: 'GET',
    parse: (v) => caseResponseSchema.parse(v),
  });
}

/** Soft delete. The row stays; the response says so. */
export function deleteCase(caseId: string): Promise<ApiResult<DeleteCaseResponse>> {
  return apiRequest(`/v1/cases/${encodeURIComponent(caseId)}`, {
    method: 'DELETE',
    parse: (v) => deleteCaseResponseSchema.parse(v),
  });
}

/* ================================================================================================
 * Recalculation (D10)
 * ============================================================================================== */

/** No side effects. Ask as often as you like. */
export function recalcPreview(caseId: string): Promise<ApiResult<RecalcPreviewResponse>> {
  return apiRequest(`/v1/cases/${encodeURIComponent(caseId)}/recalculate/preview`, {
    body: {},
    parse: (v) => recalcPreviewResponseSchema.parse(v),
  });
}

/** Archives the prior snapshot as a revision and re-stamps the case. Not reversible by the user. */
export function recalcConfirm(caseId: string): Promise<ApiResult<RecalcConfirmResponse>> {
  return apiRequest(`/v1/cases/${encodeURIComponent(caseId)}/recalculate/confirm`, {
    body: {},
    parse: (v) => recalcConfirmResponseSchema.parse(v),
  });
}

/* ================================================================================================
 * Legacy import
 * ============================================================================================== */

/** The saved file itself is the body. One file yields up to three cases (US-209). */
export function importSavedFile(
  projectId: string,
  file: unknown,
): Promise<ApiResult<ImportResponse>> {
  return apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/import`, {
    body: file,
    parse: (v) => importResponseSchema.parse(v),
  });
}
